import { call, put, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { shareActions } from '../slices/share.slice';
import {
  addMemorableMoment,
  addToShare,
  deleteMemorableMoment,
  getFanShare,
  getFanShareHistory,
  getMemorableMoments,
  getTeamShare,
  getTeamShareHistory,
  removeShareItem,
  removeShareReaction,
  setShareReaction,
} from '../../services/share.service';
import { notificationActions } from '../slices/notification.slice';

function* refreshSharePage(
  shareType: 'team' | 'fan',
  eventId: string,
): SagaIterator {
  if (!eventId) return;

  yield put({
    type:
      shareType === 'team'
        ? shareActions.getTeamShare.type
        : shareActions.getFanShare.type,
    payload: eventId,
  });
}

function* addToShareSaga({ payload }: any): SagaIterator {
  try {
    const result: any = yield call(addToShare, payload);
    const mode = result?.data?.mode;

    yield put({ type: shareActions['addToShare/ok'].type });

    yield put({
      type:
        payload?.shareType === 'team'
          ? shareActions.getTeamShareHistory.type
          : shareActions.getFanShareHistory.type,
    });

    if (payload?.qdosEventId) {
      yield call(refreshSharePage, payload.shareType, payload.qdosEventId);
    }

    yield put({
      type: notificationActions.success.type,
      payload:
        mode === 'existing'
          ? 'Already in your share page'
          : 'Added to share page',
    });
  } catch (err) {
    yield put({ type: shareActions['addToShare/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* addMemorableMomentSaga({ payload }: any): SagaIterator {
  try {
    const result: any = yield call(addMemorableMoment, payload);
    const mode = result?.data?.mode;

    yield put({ type: shareActions['addMemorableMoment/ok'].type });
    yield put({ type: shareActions.getMemorableMoments.type });
    yield put({
      type: notificationActions.success.type,
      payload: mode === 'existing' ? 'Already saved to Joy' : 'Saved to Joy',
    });
  } catch (err) {
    yield put({ type: shareActions['addMemorableMoment/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* getMemorableMomentsSaga(): SagaIterator {
  try {
    const result: any = yield call(getMemorableMoments);
    const data = Array.isArray(result) ? result : result?.data;

    yield put({
      type: shareActions['getMemorableMoments/ok'].type,
      payload: Array.isArray(data) ? data : [],
    });
  } catch (err) {
    yield put({ type: shareActions['getMemorableMoments/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* deleteMemorableMomentSaga({ payload }: any): SagaIterator {
  try {
    yield call(deleteMemorableMoment, payload);
    yield put({
      type: shareActions['deleteMemorableMoment/ok'].type,
      payload,
    });
    yield put({
      type: notificationActions.success.type,
      payload: 'Removed from Joy',
    });
  } catch (err) {
    yield put({ type: shareActions['deleteMemorableMoment/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* getTeamShareSaga({ payload }: any): SagaIterator {
  try {
    const result: any = yield call(getTeamShare, payload);
    const data = Array.isArray(result) ? result : result?.data;

    yield put({
      type: shareActions['getTeamShare/ok'].type,
      payload: Array.isArray(data) ? data : [],
    });
  } catch (err) {
    yield put({ type: shareActions['getTeamShare/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* getFanShareSaga({ payload }: any): SagaIterator {
  try {
    const result: any = yield call(getFanShare, payload);
    const data = Array.isArray(result) ? result : result?.data;

    yield put({
      type: shareActions['getFanShare/ok'].type,
      payload: Array.isArray(data) ? data : [],
    });
  } catch (err) {
    yield put({ type: shareActions['getFanShare/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* getTeamShareHistorySaga(): SagaIterator {
  try {
    const result: any = yield call(getTeamShareHistory);
    const data = result?.data ?? result;

    yield put({
      type: shareActions['getTeamShareHistory/ok'].type,
      payload: data || { currentEvent: null, previousEvents: [] },
    });
  } catch (err) {
    yield put({ type: shareActions['getTeamShareHistory/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* getFanShareHistorySaga(): SagaIterator {
  try {
    const result: any = yield call(getFanShareHistory);
    const data = result?.data ?? result;

    yield put({
      type: shareActions['getFanShareHistory/ok'].type,
      payload: data || { currentEvent: null, previousEvents: [] },
    });
  } catch (err) {
    yield put({ type: shareActions['getFanShareHistory/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* setShareReactionSaga({ payload }: any): SagaIterator {
  try {
    const result: any = yield call(
      setShareReaction,
      payload.shareItemId,
      payload.reactionType,
    );
    const mode = result?.data?.mode;

    yield put({ type: shareActions['setShareReaction/ok'].type });

    yield call(refreshSharePage, payload.shareType, payload.eventId);

    let message = 'Reaction saved';
    if (mode === 'updated') message = 'Reaction updated';
    if (mode === 'existing') message = 'Reaction already selected';

    yield put({
      type: notificationActions.success.type,
      payload: message,
    });
  } catch (err) {
    yield put({ type: shareActions['setShareReaction/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* removeShareReactionSaga({ payload }: any): SagaIterator {
  try {
    yield call(removeShareReaction, payload.shareItemId);
    yield put({ type: shareActions['removeShareReaction/ok'].type });

    yield call(refreshSharePage, payload.shareType, payload.eventId);

    yield put({
      type: notificationActions.success.type,
      payload: 'Reaction removed',
    });
  } catch (err) {
    yield put({ type: shareActions['removeShareReaction/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* removeShareItemSaga({ payload }: any): SagaIterator {
  try {
    const id = typeof payload === 'string' ? payload : payload?.id;

    yield call(removeShareItem, id);
    yield put({ type: shareActions['removeShareItem/ok'].type });

    if (payload?.shareType && payload?.eventId) {
      yield call(refreshSharePage, payload.shareType, payload.eventId);
    }

    yield put({
      type: notificationActions.success.type,
      payload: 'Removed from share page',
    });
  } catch (err) {
    yield put({ type: shareActions['removeShareItem/err'].type });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

export function* watchShare(): SagaIterator {
  yield takeLatest(shareActions.addToShare.type, addToShareSaga);
  yield takeLatest(shareActions.addMemorableMoment.type, addMemorableMomentSaga);
  yield takeLatest(
    shareActions.getMemorableMoments.type,
    getMemorableMomentsSaga,
  );
  yield takeLatest(
    shareActions.deleteMemorableMoment.type,
    deleteMemorableMomentSaga,
  );
  yield takeLatest(shareActions.getTeamShare.type, getTeamShareSaga);
  yield takeLatest(shareActions.getFanShare.type, getFanShareSaga);
  yield takeLatest(
    shareActions.getTeamShareHistory.type,
    getTeamShareHistorySaga,
  );
  yield takeLatest(
    shareActions.getFanShareHistory.type,
    getFanShareHistorySaga,
  );
  yield takeLatest(shareActions.setShareReaction.type, setShareReactionSaga);
  yield takeLatest(
    shareActions.removeShareReaction.type,
    removeShareReactionSaga,
  );
  yield takeLatest(shareActions.removeShareItem.type, removeShareItemSaga);
}